import React, { useState, useEffect, useContext, useCallback, memo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals, formatTimeWithEnglishNumerals } from '../shared';
import { Plus, Search, Edit, Trash2, FileText, Download, Printer, Eye, Upload, Calendar, DollarSign, Building2, Users, MapPin, AlertTriangle, Check, X, RefreshCw, ArrowRightLeft, ChevronDown, ChevronUp, Filter, BarChart3, Settings, Save } from 'lucide-react';

const AgencySettingsManagement = () => {
  const { t } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    phone_2: '',
    phone_3: '',
    fax: '',
    email: '',
    website: '',
    tax_number: '',
    commercial_register: '',
    national_register: '',
    business_license: '',
    manager_name: '',
    manager_email: '',
    established_date: '',
    description: '',
    logo_url: '',
    header_text: '',
    footer_text: '',
    manager_signature_url: '',
    // 🆕 Per-agency cash transfer threshold (DZD). When the current balance
    // crosses this value the dashboard turns red and asks for a transfer.
    cash_transfer_threshold: 1000000
  });

  const showToast = (message, type = 'success') => {
    // Toast implementation (you might want to use a toast library)
    console.log(`${type}: ${message}`);
  };

  // Load agencies and set initial data
  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAgencies(response.data);
      
      // For agency staff, auto-select their agency
      if (user?.role === 'agency_staff' && user?.agency_id) {
        const userAgency = response.data.find(agency => agency.id === user.agency_id);
        if (userAgency) {
          setSelectedAgency(userAgency);
          loadAgencyDetails(userAgency.id);
        }
      } else if (response.data.length > 0) {
        // For GM/GA, select first agency by default
        setSelectedAgency(response.data[0]);
        loadAgencyDetails(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading agencies:', error);
      showToast('فشل في تحميل الوكالات', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAgencyDetails = async (agencyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies/${agencyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const agency = response.data;
      setFormData({
        name: agency.name || '',
        address: agency.address || '',
        city: agency.city || '',
        postal_code: agency.postal_code || '',
        phone: agency.phone || '',
        phone_2: agency.phone_2 || '',
        phone_3: agency.phone_3 || '',
        fax: agency.fax || '',
        email: agency.email || '',
        website: agency.website || '',
        tax_number: agency.tax_number || '',
        commercial_register: agency.commercial_register || '',
        national_register: agency.national_register || '',
        business_license: agency.business_license || '',
        manager_name: agency.manager_name || '',
        manager_email: agency.manager_email || '',
        established_date: agency.established_date || '',
        description: agency.description || '',
        logo_url: agency.logo_url || '',
        header_text: agency.header_text || '',
        footer_text: agency.footer_text || '',
        manager_signature_url: agency.manager_signature_url || '',
        cash_transfer_threshold: agency.cash_transfer_threshold ?? 1000000
      });
    } catch (error) {
      console.error('Error loading agency details:', error);
      showToast('فشل في تحميل تفاصيل الوكالة', 'error');
    }
  };

  const handleAgencyChange = (agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    setSelectedAgency(agency);
    loadAgencyDetails(agencyId);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle logo file selection and preview
  const handleLogoFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('يرجى اختيار ملف صورة صالح (JPEG, PNG, GIF)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('حجم الملف كبير جداً. يرجى اختيار صورة أقل من 5 ميجابايت');
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload logo file
  const handleLogoUpload = async () => {
    if (!logoFile || !selectedAgency) return;

    try {
      setUploadingLogo(true);
      
      const formDataUpload = new FormData();
      formDataUpload.append('logo', logoFile);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/agencies/${selectedAgency.id}/upload-logo`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update form data with new logo URL
      const logoUrl = response.data.logo_url;
      setFormData(prev => ({
        ...prev,
        logo_url: logoUrl
      }));
      
      // Clear upload state
      setLogoFile(null);
      setLogoPreview(null);
      
      showToast('✅ تم رفع اللوجو بنجاح');
      
      // Reset file input
      const fileInput = document.getElementById('logo-upload');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error uploading logo:', error);
      showToast('❌ فشل في رفع اللوجو: ' + (error.response?.data?.detail || error.message), 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Remove current logo
  const handleLogoRemove = async () => {
    if (!selectedAgency || !formData.logo_url) return;
    
    if (!confirm('هل أنت متأكد من حذف اللوجو الحالي؟')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/agencies/${selectedAgency.id}/remove-logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData(prev => ({
        ...prev,
        logo_url: ''
      }));
      
      showToast('✅ تم حذف اللوجو بنجاح');
      
    } catch (error) {
      console.error('Error removing logo:', error);
      showToast('❌ فشل في حذف اللوجو', 'error');
    }
  };

  const handleSave = async () => {
    if (!selectedAgency) return;
    
    // Agency staff cannot save
    if (user?.role === 'agency_staff') {
      showToast('ليس لديك صلاحية لتعديل إعدادات الوكالة', 'error');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/agencies/${selectedAgency.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast(t('settingsUpdated'));
      loadAgencies(); // Refresh agencies list
    } catch (error) {
      console.error('Error saving agency settings:', error);
      showToast(t('settingsUpdateFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = user?.role === 'agency_staff';

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" dir="rtl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('agencySettings')}</h1>
        <p className="text-gray-600">
          {isReadOnly ? 'عرض معلومات الوكالة' : 'إدارة وتحديث معلومات الوكالات'}
        </p>
      </div>

      {/* Agency Selector (for GM/GA only) */}
      {!isReadOnly && agencies.length > 1 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>اختيار الوكالة</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedAgency?.id || ''} onValueChange={handleAgencyChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="اختر الوكالة" />
              </SelectTrigger>
              <SelectContent>
                {agencies.map(agency => (
                  <SelectItem key={agency.id} value={agency.id}>
                    {agency.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {selectedAgency && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t('basicInformation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">{t('agencyName')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="address">{t('agencyAddress')}</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  disabled={isReadOnly}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">{t('agencyCity')}</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code">{t('postalCode')}</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => handleInputChange('postal_code', e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description">{t('agencyDescription')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  disabled={isReadOnly}
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="established_date">{t('establishedDate')}</Label>
                <Input
                  id="established_date"
                  type="date"
                  value={formData.established_date}
                  onChange={(e) => handleInputChange('established_date', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t('contactInformation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="phone">{t('primaryPhone')}</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="phone_2">{t('secondaryPhone')}</Label>
                <Input
                  id="phone_2"
                  value={formData.phone_2}
                  onChange={(e) => handleInputChange('phone_2', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="phone_3">{t('additionalPhone')}</Label>
                <Input
                  id="phone_3"
                  value={formData.phone_3}
                  onChange={(e) => handleInputChange('phone_3', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="fax">{t('faxNumber')}</Label>
                <Input
                  id="fax"
                  value={formData.fax}
                  onChange={(e) => handleInputChange('fax', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="email">{t('agencyEmail')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="website">{t('agencyWebsite')}</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </CardContent>
          </Card>

          {/* Logo Management */}
          <Card>
            <CardHeader>
              <CardTitle>🎨 إدارة اللوجو</CardTitle>
              <CardDescription>
                رفع وإدارة لوجو الوكالة الذي سيظهر في جميع الوثائق والوصولات
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Logo Display */}
              {formData.logo_url ? (
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <Label className="font-medium text-gray-700 mb-2 block">اللوجو الحالي:</Label>
                  <div className="flex items-center space-x-4 rtl:space-x-reverse">
                    <img 
                      src={
                        // logo_url from backend is a path like
                        // "/uploads/logos/xxx.png". Prepend the API base so
                        // the browser hits the FastAPI route, not the SPA.
                        formData.logo_url?.startsWith('http')
                          ? formData.logo_url
                          : `${API}${formData.logo_url}`
                      }
                      alt="Logo" 
                      className="w-20 h-20 object-contain bg-white border rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div style={{display: 'none'}} className="w-20 h-20 bg-gray-200 border rounded flex items-center justify-center text-gray-500 text-xs">
                      صورة غير متاحة
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-2">
                        اللوجو الحالي سيظهر في جميع الوصولات والطباعات
                      </p>
                      {!isReadOnly && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleLogoRemove}
                          className="text-red-600 hover:text-red-700"
                        >
                          🗑️ حذف اللوجو
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-brand-50 p-4 rounded-lg border border-brand-200">
                  <div className="flex items-center space-x-3 rtl:space-x-reverse text-brand-700">
                    <div className="w-12 h-12 bg-brand-200 rounded-full flex items-center justify-center">
                      🎨
                    </div>
                    <div>
                      <p className="font-medium">لا يوجد لوجو حالياً</p>
                      <p className="text-sm">قم برفع لوجو الوكالة ليظهر في جميع الوثائق</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Logo Upload Section */}
              {!isReadOnly && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="logo-upload" className="font-medium">رفع لوجو جديد:</Label>
                    <div className="mt-2">
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoFileSelect}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        الملفات المدعومة: PNG, JPG, GIF (أقل من 5 ميجابايت)
                      </p>
                    </div>
                  </div>

                  {/* Logo Preview */}
                  {logoPreview && (
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <Label className="font-medium text-green-700 mb-2 block">معاينة اللوجو الجديد:</Label>
                      <div className="flex items-center space-x-4 rtl:space-x-reverse">
                        <img 
                          src={logoPreview} 
                          alt="Logo Preview" 
                          className="w-20 h-20 object-contain bg-white border rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm text-green-700 mb-3">
                            اللоجو جاهز للرفع. اضغط "رفع اللوجو" لحفظه.
                          </p>
                          <div className="flex space-x-2 rtl:space-x-reverse">
                            <Button 
                              onClick={handleLogoUpload}
                              disabled={uploadingLogo}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              size="sm"
                            >
                              {uploadingLogo ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  جاري الرفع...
                                </>
                              ) : (
                                '📤 رفع اللوجو'
                              )}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setLogoFile(null);
                                setLogoPreview(null);
                                document.getElementById('logo-upload').value = '';
                              }}
                            >
                              إلغاء
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Logo URL Input (fallback for manual entry) */}
              <div>
                <Label htmlFor="logo_url">رابط اللوجو (اختياري):</Label>
                <Input
                  id="logo_url"
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  disabled={isReadOnly}
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-gray-500 mt-1">
                  يمكنك إدخال رابط مباشر للوجو بدلاً من رفع ملف
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Registration Details */}
          <Card>
            <CardHeader>
              <CardTitle>{t('registrationDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="tax_number">{t('taxNumber')}</Label>
                <Input
                  id="tax_number"
                  value={formData.tax_number}
                  onChange={(e) => handleInputChange('tax_number', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="commercial_register">{t('commercialRegister')}</Label>
                <Input
                  id="commercial_register"
                  value={formData.commercial_register}
                  onChange={(e) => handleInputChange('commercial_register', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="national_register">{t('nationalRegister')}</Label>
                <Input
                  id="national_register"
                  value={formData.national_register}
                  onChange={(e) => handleInputChange('national_register', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="business_license">{t('businessLicense')}</Label>
                <Input
                  id="business_license"
                  value={formData.business_license}
                  onChange={(e) => handleInputChange('business_license', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </CardContent>
          </Card>

          {/* Management & Branding */}
          <Card>
            <CardHeader>
              <CardTitle>{t('managementInfo')} & {t('brandingSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="manager_name">{t('managerName')}</Label>
                <Input
                  id="manager_name"
                  value={formData.manager_name}
                  onChange={(e) => handleInputChange('manager_name', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="manager_email">📧 بريد المدير (لاستلام التقارير اليومية)</Label>
                <Input
                  id="manager_email"
                  type="email"
                  placeholder="manager@example.com"
                  value={formData.manager_email}
                  onChange={(e) => handleInputChange('manager_email', e.target.value)}
                  disabled={isReadOnly}
                  data-testid="agency-manager-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  يُستخدم لإرسال تقرير العمليات اليومية بصيغة PDF من زر "إرسال بالبريد" في صفحة التقارير.
                </p>
              </div>
              {/* 🆕 Per-agency cash-transfer alert threshold. Drives the
                  red strip on the Dashboard ("الحالة المالية"). Editable
                  only by super_admin / general_accountant. */}
              <div>
                <Label htmlFor="cash_transfer_threshold">
                  🚨 حدّ تنبيه تحويل النقدية (دج)
                </Label>
                <Input
                  id="cash_transfer_threshold"
                  type="number"
                  min="0"
                  step="50000"
                  value={formData.cash_transfer_threshold}
                  onChange={(e) => handleInputChange(
                    'cash_transfer_threshold',
                    e.target.value === '' ? 0 : Number(e.target.value),
                  )}
                  disabled={isReadOnly}
                  data-testid="agency-cash-threshold-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  عند تجاوز هذا المبلغ في رصيد الوكالة الحالي، تظهر تنبيه أحمر على لوحة التحكم
                  ينصح بتحويل النقدية إلى الإدارة العامة. الافتراضي: 1,000,000 دج.
                </p>
              </div>
              <div>
                <Label htmlFor="logo_url">{t('logoUrl')}</Label>
                <Input
                  id="logo_url"
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label htmlFor="header_text">{t('headerText')}</Label>
                <Textarea
                  id="header_text"
                  value={formData.header_text}
                  onChange={(e) => handleInputChange('header_text', e.target.value)}
                  disabled={isReadOnly}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="footer_text">{t('footerText')}</Label>
                <Textarea
                  id="footer_text"
                  value={formData.footer_text}
                  onChange={(e) => handleInputChange('footer_text', e.target.value)}
                  disabled={isReadOnly}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="manager_signature_url">{t('managerSignature')}</Label>
                <Input
                  id="manager_signature_url"
                  type="url"
                  value={formData.manager_signature_url}
                  onChange={(e) => handleInputChange('manager_signature_url', e.target.value)}
                  disabled={isReadOnly}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Save Button (only for GM/GA) */}
      {!isReadOnly && selectedAgency && (
        <div className="mt-6 flex justify-end">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-brand-600 hover:bg-brand-700"
          >
            {saving ? 'جاري الحفظ...' : t('saveSettings')}
          </Button>
        </div>
      )}
    </div>
  );
};

// Daily Operations Management Component


export default AgencySettingsManagement;
